// Student PaymentsPage — own payment history, with M-Pesa instructions and code submission
// for payments that still need paying. Refreshes itself while any payment is pending, so the
// status changes as soon as an admin verifies the submitted code.
import { Fragment, useState } from "react";
import { useFetch } from "../../hooks/useApi.js";
import { useJustVerifiedPayment } from "../../hooks/useJustVerifiedPayment.js";
import TransactionStatusBadge from "../../components/shared/TransactionStatusBadge.js";
import MpesaInstructions from "../../components/student/MpesaInstructions.js";
import PaymentConfirmedBanner from "../../components/student/PaymentConfirmedBanner.js";
import { PAYMENT_POLL_INTERVAL_MS } from "@dm-lms/shared/constants";
import type { PaymentWithCourse } from "@dm-lms/shared/types";

const COLUMN_COUNT = 7;   // keep in step with the <th> cells below (expanded row spans them all)

// Pending payments still need paying or verifying; failed ones can be resubmitted. Paid ones need nothing.
function needsPaymentAction(payment: PaymentWithCourse): boolean {
  return payment.status === "pending" || payment.status === "failed";
}

export default function PaymentsPage() {
  const { data, isLoading, error, refresh } = useFetch<PaymentWithCourse[]>("/api/payments/history", {
    pollMs: PAYMENT_POLL_INTERVAL_MS,
    pollWhile: (payments) => payments.some((payment) => payment.status === "pending"),
  });
  const [expandedId, setExpandedId] = useState<string | null>(null);   // one row's instructions at a time
  const { justVerifiedCode, dismiss } = useJustVerifiedPayment(data);
  const justVerified = justVerifiedCode ? data?.find((payment) => payment.code === justVerifiedCode) : undefined;

  return (
    <div className="dm-page">
      <h1 className="text-2xl font-semibold text-dm-navy mb-6">Payments</h1>

      {justVerified && <PaymentConfirmedBanner payment={justVerified} onDismiss={dismiss} />}

      {isLoading && <p className="text-dm-slate text-sm">Loading payments…</p>}
      {error && <p className="text-red-600 text-sm">{error}</p>}

      {!isLoading && !error && (
        (data ?? []).length === 0 ? (
          <p className="text-dm-slate text-sm">No payments yet.</p>
        ) : (
          <div className="overflow-x-auto rounded-lg border border-gray-200 bg-white">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 text-left text-dm-slate">
                <tr>
                  <th className="px-4 py-3 font-medium">Code</th>
                  <th className="px-4 py-3 font-medium">Course</th>
                  <th className="px-4 py-3 font-medium">Amount (KES)</th>
                  <th className="px-4 py-3 font-medium">Method</th>
                  <th className="px-4 py-3 font-medium">Status</th>
                  <th className="px-4 py-3 font-medium">Paid</th>
                  <th className="px-4 py-3 font-medium">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {(data ?? []).map((payment) => {
                  const canAct = needsPaymentAction(payment);
                  const isFailed = payment.status === "failed";
                  const isPaid = payment.status === "success";
                  const isExpanded = expandedId === payment.id;
                  // A card the student already had open stays, turning into the confirmation when paid
                  const showCard = isExpanded && (canAct || isPaid);

                  return (
                    <Fragment key={payment.id}>
                      <tr>
                        <td className="px-4 py-3 text-dm-navy font-medium">{payment.code}</td>
                        <td className="px-4 py-3">{payment.courseTitle ?? "—"}</td>
                        <td className="px-4 py-3">{payment.amountKes.toLocaleString()}</td>
                        <td className="px-4 py-3 uppercase">{payment.method}</td>
                        <td className="px-4 py-3">
                          <TransactionStatusBadge status={payment.status} hasReference={payment.reference !== null} />
                        </td>
                        <td className="px-4 py-3">{payment.paidAt ? new Date(payment.paidAt).toLocaleDateString() : "—"}</td>
                        <td className="px-4 py-3">
                          {(canAct || showCard) && (
                            <button
                              type="button"
                              aria-expanded={isExpanded}
                              onClick={() => { setExpandedId(isExpanded ? null : payment.id); }}
                              className="text-sm font-medium text-dm-royal underline-offset-2 hover:underline"
                            >
                              {isExpanded
                                ? isPaid ? "Hide" : "Hide payment instructions"
                                : isFailed
                                  ? "Resubmit payment code"
                                  : "View payment instructions"}
                            </button>
                          )}
                        </td>
                      </tr>
                      {showCard && (
                        <tr>
                          <td colSpan={COLUMN_COUNT} className="bg-gray-50 px-4 py-3">
                            <MpesaInstructions
                              paymentId={payment.id}
                              initialReference={payment.reference}
                              verificationFailed={isFailed}
                              paid={isPaid}
                              amount={payment.amountKes}
                              paymentCode={payment.code}
                              courseAccountRef={payment.courseAccountRef}
                              onSubmitted={refresh}
                              onDismiss={() => { setExpandedId(null); }}
                            />
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )
      )}
    </div>
  );
}
